<template>
  <div class="r2-upload-component">
    <!-- 已上传文件预览 -->
    <div class="imgactive" v-show="uploadedFileUrl !== '' && showPreview">
      <!-- 图片预览 -->
      <img v-if="isImageFile" style="width:100px;height:100px;object-fit: contain;" :src="uploadedFileUrl"/>
      
      <!-- Excel/CSV文件预览 -->
      <div v-else class="file-preview">
        <div class="file-icon">📊</div>
        <div class="file-name">{{ getFileName() }}</div>
      </div>
      
      <span class="imgactiveafter" @click="removeUploadedImg">x</span>
    </div>
    
    <!-- 上传文件区域 -->
    <div class="ccpay-add-img" v-if="!hideUploadArea && uploadedFileUrl === ''">
      <input 
        multiple 
        type="file" 
        :accept="acceptTypes" 
        class="img-btn" 
        ref="fileInput" 
        @change="handleFileSelect" 
      />
    </div>
    
    <!-- 自定义插槽，允许父组件自定义上传UI -->
    <slot name="upload-area" :uploadFile="triggerUpload" :loading="loading" v-if="uploadedFileUrl === ''">
      <!-- 默认上传按钮 -->
      <button 
        v-if="hideUploadArea"
        :disabled="loading" 
        @click="triggerUpload" 
        class="upload-btn"
      >
        {{ loading ? '上传中...' : buttonText }}
      </button>
    </slot>

    <!-- 加载状态 -->
    <div v-show="loading && showSpin" class="loading-overlay">
      <div class="loading-spinner">上传中...</div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "CloudflareR2Upload",
  props: {
    // 后端API端点
    apiEndpoint: {
      type: String,
      default: '/v1/test/generate_upload_url'
    },
    // 文件夹路径前缀
    folderPrefix: {
      type: String,
      default: 'uploads/'
    },
    // 允许的文件类型
    acceptTypes: {
      type: String,
      default: 'image/*,.xls,.xlsx,.csv'
    },
    // 最大文件大小(KB)
    maxFileSize: {
      type: Number,
      default: 1000000
    },
    // 按钮文本
    buttonText: {
      type: String,
      default: '上传文件'
    },
    // 按钮类型
    buttonType: {
      type: String,
      default: 'primary'
    },
    // 是否隐藏默认上传区域
    hideUploadArea: {
      type: Boolean,
      default: false
    },
    // 是否显示Spin加载
    showSpin: {
      type: Boolean,
      default: true
    },
    // 是否显示预览
    showPreview: {
      type: Boolean,
      default: true
    },
    // 允许的MIME类型
    allowedMimeTypes: {
      type: Array,
      default: () => [
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif',
        'image/webp',
        'image/bmp',
        'image/svg+xml',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ]
    }
  },
  data() {
    return {
      loading: false,
      currentFile: null,
      uploadedFileUrl: '' // 存储上传成功的文件URL
    };
  },
  computed: {
    // 判断是否为图片文件
    isImageFile() {
      if (!this.currentFile) return false;
      const imageTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 
        'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'
      ];
      return imageTypes.includes(this.currentFile.type);
    }
  },
  methods: {
    // 触发文件选择
    triggerUpload() {
      this.$refs.fileInput.click();
    },

    // 处理文件选择
    handleFileSelect(event) {
      const files = event.target.files;
      if (!files || files.length === 0) {
        this.$Toast("请选择文件");
        return;
      }

      const file = files[0];
      if (!this.validateFile(file)) {
        return;
      }

      this.currentFile = file;
      this.startUpload();
    },

    // 验证文件
    validateFile(file) {
      // 检查文件大小
      const fileSizeKB = file.size / 1024;
      if (fileSizeKB > this.maxFileSize) {
        this.$Toast(`请上传小于${this.maxFileSize}KB的文件`);
        return false;
      }

      // 检查文件类型
      if (!this.allowedMimeTypes.includes(file.type)) {
        this.$Toast("请选择正确的文件类型");
        return false;
      }

      return true;
    },

    // 开始上传
    startUpload() {
      if (!this.currentFile) {
        this.$Toast('没有文件可上传');
        return;
      }

      this.loading = true;
      
      console.log('开始上传文件:', {
        fileName: this.currentFile.name,
        fileType: this.currentFile.type,
        fileSize: this.currentFile.size
      });

      // 发出上传开始事件
      this.$emit('upload-start', {
        file: this.currentFile,
        filename: this.currentFile.name
      });
      
      // 使用预签名URL上传
      this.uploadWithPresignedUrl(this.currentFile);
    },

    // 使用预签名URL上传（调用后端API）
    async uploadWithPresignedUrl(file) {
      try {
        console.log('请求后端生成预签名URL...');
        
        // 1. 调用后端API获取预签名URL
        const response = await this.$axios.post(this.apiEndpoint, {
          filename: file.name,
          contentType: file.type,
          folderPrefix: this.folderPrefix
        });

        if (response.code !== 0) {
          throw new Error(response.msg || '获取上传链接失败');
        }

        const { uploadUrl, uploadFields, fileUrl, filename } = response.data;
        
        console.log('预签名URL获取成功，开始上传文件...');

        // 2. 使用预签名URL上传到R2
        let uploadResponse;
        
        if (uploadFields) {
          // 使用 createPresignedPost 方式（支持条件限制）
          const formData = new FormData();
          
          // 添加预签名的字段
          Object.keys(uploadFields).forEach(key => {
            formData.append(key, uploadFields[key]);
          });
          
          // 添加文件（必须最后添加）
          formData.append('file', file);
          
          uploadResponse = await fetch(uploadUrl, {
            method: 'POST',
            body: formData
          });
        } else {
          // 使用 getSignedUrl 方式（简单PUT请求）
          uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type
            }
          });
        }

        if (!uploadResponse.ok) {
          throw new Error(`上传失败: HTTP ${uploadResponse.status} ${uploadResponse.statusText}`);
        }
        
        console.log('文件上传成功');
        
        // 3. 处理上传成功
        this.handleUploadSuccess({ Location: fileUrl }, filename);

      } catch (error) {
        console.error('预签名URL上传失败:', error);
        this.loading = false;
        this.$Toast('上传失败: ' + error.message);
        
        this.$emit('upload-error', {
          error: error,
          type: 'presigned-api'
        });
      }
    },

    // 处理上传成功
    handleUploadSuccess(result, filename) {
      const fileUrl = result.Location;

      console.log('Cloudflare R2上传成功:', fileUrl);
      this.loading = false;
      this.$Toast('文件上传成功');

      // 存储上传成功的文件URL到组件内部
      this.uploadedFileUrl = fileUrl;

      // 发出上传成功事件
      this.$emit('upload-success', fileUrl, {
        url: fileUrl,
        filename: filename,
        originalFile: this.currentFile,
        fileType: this.currentFile.type,
        fileName: this.currentFile.name
      });

      // 清理文件选择
      this.$refs.fileInput.value = '';
      this.currentFile = null;
    },

    // 删除已上传的文件
    removeUploadedImg() {
      this.uploadedFileUrl = '';
      this.$Toast('文件已删除');
      
      // 发出删除事件
      this.$emit('upload-remove', {
        message: '文件已删除'
      });
    },

    // 获取文件名（去掉路径和时间戳）
    getFileName() {
      if (!this.currentFile) return '';
      return this.currentFile.name;
    }
  }
};
</script>

<style lang="scss" scoped>
.r2-upload-component {
  position: relative;
  text-align: center;
  width: 100%;
  height: 100px;
}

// 预览图片/文件样式
.imgactive {
  position: absolute;
  width: 100px;
  height: 100px;
  left: 50%;
  margin-left: -50px;
  
  img {
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .file-preview {
    width: 100px;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    .file-icon {
      font-size: 30px;
      margin-bottom: 5px;
    }
    
    .file-name {
      font-size: 10px;
      color: #666;
      text-align: center;
      word-break: break-all;
      overflow: hidden;
      text-overflow: ellipsis;
      max-height: 30px;
      line-height: 12px;
      padding: 0 5px;
    }
  }
}

.imgactiveafter {
  position: absolute;
  right: -20px;
  top: 0;
  padding-left: 2px;
  padding-right: 2px;
  font-size: 1.2rem;
  color: #ccc;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #ff776d;
    background: rgba(255, 255, 255, 1);
  }
}

.ccpay-add-img {
  background: white;
  position: relative;
  width: 100%;
  height: 100px;
  margin-bottom: 0.5rem;

  input {
    position: absolute;
    width: 100px;
    height: 100px;
    left: 50%;
    margin-left: -50px;
  }

  .img-btn {
    border: none !important;
    overflow: hidden;
    
    &::after {
      content: "";
      padding: 0 !important;
      background: url("../../static/mobile_img/addimg.png") #fff;
      width: 100px;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      background-size: cover;
    }
  }
}

.upload-btn {
  background: #ff776d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  color: #ff776d;
  font-size: 14px;
}
</style> 


// WEBPACK FOOTER //
// src/components/CloudflareR2Upload.vue